import { FC, useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@hooks/store';
import { getSuperSimplifierAccountsAsync, getSuperSimplifierAccountAsync } from '@store/reducers/supersimplifier/thunks';
import { ScreenState } from '@src/app/enums';
import { SupersimplifierStore, selectSupersimplifier, setSuperSimplifierAccountParameter } from '@store/reducers/supersimplifier';
import Container from '@mui/material/Container';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { PageContent } from '@components/index';
import SupersimplifierTable, { SupersimplifierTableRow, TableHeaders } from '@components/superTables/supersimplifierTable';
import { homeUrl } from '@utils/UrlBuilder';
import { Alert, Typography, Breadcrumbs, Box, FormControl, InputLabel } from '@mui/material';
import { SuperSimplifierAccountSummary } from 'clientportalshared';
import './styles.scss';
import { useHistory } from 'react-router';
import { Home } from '@mui/icons-material';

const BenefitStatement: FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const supersimplifierStore: SupersimplifierStore = useAppSelector(selectSupersimplifier);

  const [selectedAccount, setSelectedAccount] = useState('');
  const history = useHistory();

  const getSuperSimplifierAccount = async (accountLis: SuperSimplifierAccountSummary[] | undefined) => {
    if (accountLis?.length && accountLis[0].accountNumber) {
      await dispatch(
        getSuperSimplifierAccountAsync({
          accountNumber: accountLis[0].accountNumber,
        })
      );
    }
  };

  const getPageData = () => {
    dispatch(getSuperSimplifierAccountsAsync())
      .unwrap()
      .then((responce) => {
        if (responce?.data?.length && responce.data[0].accountNumber) {
          setSelectedAccount(responce.data[0].accountNumber);
        }
        getSuperSimplifierAccount(responce.data);
      });
  };

  useEffect(() => {
    getPageData();
  }, []);

  const tableHeaders: TableHeaders = { header1: 'Description', header2: 'Value' };
  let coverSummaryTable: SupersimplifierTableRow[] = [];
  let taxTable: SupersimplifierTableRow[] = [];
  let preservationTable: SupersimplifierTableRow[] = [];

  if (supersimplifierStore.superSimplifierAccount?.benefits && supersimplifierStore.superSimplifierAccount?.insurances) {
    const {
      preservationComponentPreservedAmount,
      preservationComponentRestrictedNonPreservedAmount,
      preservationComponentUnrestrictedNonPreservedAmount,
      taxationComponentEligibleServiceDate,
      taxationComponentTaxFreeComponentAmount,
      taxationComponentTaxableComponentTaxedAmount,
      taxationComponentTaxableComponentUntaxedAmount,
    } = supersimplifierStore.superSimplifierAccount?.benefits;

    const taxTotal =
      (taxationComponentTaxFreeComponentAmount ?? 0) +
      (taxationComponentTaxableComponentUntaxedAmount ?? 0) +
      (taxationComponentTaxableComponentTaxedAmount ?? 0);

    taxTable = [
      { label: 'Eligible Service Date', value: taxationComponentEligibleServiceDate },
      { label: 'Tax Free Component', value: taxationComponentTaxFreeComponentAmount },
      {
        label: 'Taxable Component (Untaxed)',
        value: taxationComponentTaxableComponentUntaxedAmount,
      },
      { label: 'Taxable Component (Taxed)', value: taxationComponentTaxableComponentTaxedAmount },
      { label: 'Total', value: taxTotal },
    ];

    const preservationTotal =
      (preservationComponentPreservedAmount ?? 0) +
      (preservationComponentRestrictedNonPreservedAmount ?? 0) +
      (preservationComponentUnrestrictedNonPreservedAmount ?? 0);

    preservationTable = [
      { label: 'Preserved', value: preservationComponentPreservedAmount },
      {
        label: 'Restricted, non-preserved (RNP)',
        value: preservationComponentRestrictedNonPreservedAmount,
      },
      {
        label: 'Unrestricted, non-preserved (UNP)',
        value: preservationComponentUnrestrictedNonPreservedAmount,
      },
      { label: 'Total', value: preservationTotal },
    ];

    const { deathInsuredAmount, totalPermanentDisabilityInsuredAmount } = supersimplifierStore.superSimplifierAccount?.insurances[0];

    coverSummaryTable = [
      {
        label: 'Death Benefit (including any insurance amount)',
        value: (deathInsuredAmount || 0) + taxTotal,
      },
      {
        label: 'Total and Permanent Disablement Benefit (including any insurance amount)',
        value: (totalPermanentDisabilityInsuredAmount || 0) + taxTotal,
      },
    ];
  }

  const handleAccountChange = (event: SelectChangeEvent) => {
    setSelectedAccount(event.target.value);
    dispatch(setSuperSimplifierAccountParameter(event.target.value));
    dispatch(
      getSuperSimplifierAccountAsync({
        accountNumber: event.target.value,
      })
    );
  };

  let errorMessage = null;
  if (supersimplifierStore.superSimplifierAccountState === ScreenState.ErrorUnableToLoadData) {
    errorMessage =
      'There is currently no benefit statement information for this account. If the account has been recently established it may take a few days for this data to be populated. Please contact your adviser or our client service team for assistance.';
    preservationTable = [];
    taxTable = [];
    coverSummaryTable = [];
  } else if (supersimplifierStore.superSimplifierAccountState === ScreenState.Loading) {
    preservationTable = [];
    taxTable = [];
    coverSummaryTable = [];
  }

  return (
    <Container maxWidth={false}>
      <Box display="flex" flexDirection="column" gap="24px" mb={6}>
        <Box display="flex" alignItems="center" gap="16px" mt={2}>
          <Breadcrumbs>
            <Typography
              onClick={() => history.push(homeUrl())}
              sx={{
                display: 'flex',
                alignItems: 'center',
                ':hover': {
                  cursor: 'pointer',
                },
              }}
              color="inherit"
            >
              <Home sx={{ mr: 0.5 }} fontSize="small" />
              Dashboard
            </Typography>
            <Typography fontWeight={600} fontSize="20px" color="#222222">
              Benefit Statement
            </Typography>
          </Breadcrumbs>

          <Box flex={1} />
          <FormControl>
            <InputLabel id="selected-account-select-label">Account</InputLabel>
            <Select
              labelId="selected-account-select-label"
              id="selected-account-select"
              value={selectedAccount}
              defaultValue={selectedAccount}
              label="Account"
              onChange={handleAccountChange}
            >
              {supersimplifierStore?.superSimplifierAccounts?.data?.map((i) => (
                <MenuItem key={i.accountNumber} value={i.accountNumber ?? ''}>{`${i.accountNumber ?? ''} - ${i.name ?? ''}`}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {errorMessage && (
          <Alert variant="outlined" severity="info">
            {errorMessage}
          </Alert>
        )}
        {!errorMessage && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            <PageContent skeletons={1} isLoading={supersimplifierStore.superSimplifierAccountState === ScreenState.Loading}>
              <SupersimplifierTable tableHeaders={tableHeaders} data={preservationTable} header="Preservation Components" />
            </PageContent>
            <PageContent skeletons={1} isLoading={supersimplifierStore.superSimplifierAccountState === ScreenState.Loading}>
              <SupersimplifierTable tableHeaders={tableHeaders} data={taxTable} header="Taxation Components" />
            </PageContent>
            <PageContent skeletons={1} isLoading={supersimplifierStore.superSimplifierAccountState === ScreenState.Loading}>
              <SupersimplifierTable tableHeaders={tableHeaders} data={coverSummaryTable} header="Other Benefits" />
            </PageContent>
            <PageContent>
              <p className="noticeParagraph">
                The values above are an estimated account balance based on information provided by the Member Administrator for Super Simplifier.
              </p>

              <p className="noticeParagraph">The estimated account balance includes:</p>

              <div>
                <ul className="noticeList">
                  <li className="noticeLi">All contributions that have been received and allocated. </li>
                  <li className="noticeLi">Fees, charges and taxes that have already been deducted from the member’s account.</li>
                </ul>
              </div>

              <p className="noticeParagraph">Some transactions may not have been added to the estimated account balance. </p>

              <p className="noticeParagraph">
                For example, if the member has made a recent contribution, we may not have received and allocated the payment into their account yet.
                Also, this account balance may not yet include all deductions for taxes, fees and costs because these are applied at specific times.
              </p>

              <p className="noticeParagraph">
                The value of your investment returns, fees and other costs are confirmed and applied to the member annual statement each year or an
                exit statement if the member closes their account.
              </p>
            </PageContent>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default BenefitStatement;
